import type { ReactNode } from 'react';
import { Loader2, PanelLeft, PanelRight } from 'lucide-react';

import type { TtqWorkbenchFocusEmphasis } from './TtqWorkbenchFocusLayout';

type TtqWorkbenchToolbarProps = {
  children?: ReactNode;
  showFocusToggle?: boolean;
  leftLabel?: string;
  rightLabel?: string;
  emphasis?: TtqWorkbenchFocusEmphasis;
  onEmphasisChange?: (emphasis: TtqWorkbenchFocusEmphasis) => void;
};

/** 品题工作台顶栏右侧工具区：分栏切换 + 品题动作按钮 */
export function TtqWorkbenchToolbar({
  children,
  showFocusToggle = false,
  leftLabel = '源题',
  rightLabel = '衍生',
  emphasis = 'left',
  onEmphasisChange,
}: TtqWorkbenchToolbarProps) {
  return (
    <div className="flex w-full flex-wrap items-center justify-end gap-2">
      {showFocusToggle && onEmphasisChange ? (
        <div className="hidden items-center gap-1.5 lg:inline-flex">
          <span className="hidden text-xs text-muted sm:inline">分栏侧重</span>
          <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
            <button
              type="button"
              className={
                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors '
                + (emphasis === 'left' ? 'bg-primary text-white' : 'text-ink hover:bg-canvas')
              }
              onClick={() => onEmphasisChange('left')}
              aria-pressed={emphasis === 'left'}
            >
              <PanelLeft size={14} />
              <span className="hidden sm:inline">强调{leftLabel}</span>
            </button>
            <button
              type="button"
              className={
                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors '
                + (emphasis === 'right' ? 'bg-primary text-white' : 'text-ink hover:bg-canvas')
              }
              onClick={() => onEmphasisChange('right')}
              aria-pressed={emphasis === 'right'}
            >
              <PanelRight size={14} />
              <span className="hidden sm:inline">强调{rightLabel}</span>
            </button>
          </div>
        </div>
      ) : null}

      {children ? (
        <div className="flex flex-wrap items-center gap-2">
          {children}
        </div>
      ) : null}
    </div>
  );
}

type TtqWorkbenchToolbarActionProps = {
  children: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
};

/** 品题工作台 toolbar 上的主操作按钮 */
export function TtqWorkbenchToolbarAction({
  children,
  disabled,
  loading,
  onClick,
}: TtqWorkbenchToolbarActionProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
      onClick={onClick}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : null}
      {children}
    </button>
  );
}
